
import {
  computed,
  defineComponent, onMounted, ref, watch,
} from 'vue';
import {
  IImageCrop, IFileUpload, ISearchLabelInput,
} from '@/types/interfaces';
import AppLoader from '@/components/UI/AppLoader.vue';
import AppTakePhoto from '@/components/AppTakePhoto.vue';
import AppMessagePhotoSelector from '@/components/AppMessagePhotoSelector.vue';
import AppMessageFileSelector from '../AppMessageFileSelector.vue';
import WebsocketService from '@/services/WebsocketService';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import MessagePhotoSelectorPopUp from '@/modules/messages/components/MessagePhotoSelectorPopUp.vue';
import MessageFileSelectorPopUp from '@/modules/messages/components/MessageFileSelectorPopUp.vue';
import MessageAddMenuPopUp from '@/modules/messages/components/MessageAddMenuPopUp.vue';
import { useMainStore } from '@/store/mainStore';
import { useFlutterBus } from '@/store/flutterBus';
import UseUpload from '@/composables/UseUpload';
import UploadService from '@/services/UploadService';
import AppUploadInfo from '@/components/UI/AppUploadInfo.vue';
import AppMessageCaseSelector from '@/components/AppMessageCaseSelector.vue';
import DesktopMessageCaseSelector from '@/components/desktop/DesktopMessageCaseSelector.vue';
import DesktopMessagePhotoSelector from '@/components/desktop/DesktopMessagePhotoSelector.vue';
import DesktopMessageFileSelector from '@/components/desktop/DesktopMessageFileSelector.vue';
import { UPLOAD_PLACES } from '@/types/enums';
import { blobToUrl, getVideoThumbnail } from '@/helpers/videoThumbnail';
import useFlutter from '@/composables/UseFlutter';

export default defineComponent({
  emits: ['send', 'onFocus'],
  components: {
    AppTakePhoto,
    AppMessagePhotoSelector,
    AppMessageFileSelector,
    AppLoader,
    MessagePhotoSelectorPopUp,
    MessageFileSelectorPopUp,
    MessageAddMenuPopUp,
    AppUploadInfo,
    AppMessageCaseSelector,
    DesktopMessageCaseSelector,
    DesktopMessagePhotoSelector,
    DesktopMessageFileSelector,
  },
  props: {
    isGroup: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    noSearch: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const addMenuPopUp = ref();
    const mediaSelectorPopUp = ref();
    const fileSelectorPopUp = ref();

    const message = ref('');
    const mediaLoadedKey = ref(0);
    const mediaToCrop = ref({} as IImageCrop);
    const mediaInputRef = ref();
    const phoneMedia = ref();
    const messagesStore = useMessagesStore();
    const mainStore = useMainStore();
    const flutterBus = useFlutterBus();

    const showFileSelector = ref(false);
    const showMediaSelector = ref(false);
    const showCaseSelector = ref(false);
    const currentChooserType = ref('');

    const mediaUploading = ref(false);
    const input = ref<HTMLElement | null>(null);

    const { onUploadUpdate, uploadInfo } = UseUpload();

    const loaderText = computed(
      () => '',
    );

    async function addMedia(e: any) {
      const img: File = (e.target as HTMLInputElement).files![0];
      let poster = '' as unknown | undefined | string;

      if (img.type.includes('video')) {
        // isMediaProcessing.value = true;
        const thumbnailBlob = await getVideoThumbnail(img, 1.0);
        poster = blobToUrl(thumbnailBlob);
      }

      // isMediaProcessing.value = false;

      mediaToCrop.value = {
        url: URL.createObjectURL(img),
        fileName: img.name,
        fileType: img.type,
        poster,
      };
      mediaLoadedKey.value += 1;

      mediaInputRef.value!.value = '';
    }

    const { flutterAddMedia } = useFlutter();
    watch(() => flutterBus.flutterCameraFile, (newValue) => {
      if (newValue) {
        flutterAddMedia(newValue).then((r) => {
          mediaToCrop.value = r;
        });
        flutterBus.flutterCameraFile = null;
      }
    });

    function handleMediaCancel() {
      mediaToCrop.value = {} as IImageCrop;
    }

    async function uploadFile(file: File) {
      console.log('UPLOAD FILE');
      // mediaUploading.value = true;

      const convId = messagesStore.currentConversation!.info.id;
      const fileUploads = UploadService.toFileUploads([file] as unknown as FileList);

      UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.DIALOG, id: convId })
        .then((response) => {
          const res = response[0];
          WebsocketService.sendMedia(
            convId,
            res.id,
            props.isGroup,
          );
        })
        .finally(() => {
          mediaUploading.value = false;
        });
    }

    async function uploadFiles(files: File[]) {
      console.log('UPLOAD FILES');
      // mediaUploading.value = true;

      const convId = messagesStore.currentConversation!.info.id;
      const fileUploads = UploadService.toFileUploads(files as unknown as FileList);

      UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.DIALOG, id: convId })
        .then((response) => {
          for (let i = 0; i < response.length; i += 1) {
            const res = response[i];
            WebsocketService.sendMedia(
              convId,
              res.id,
              props.isGroup,
            );
          }
        })
        .finally(() => {
          mediaUploading.value = false;
        });
    }

    watch(() => flutterBus.flutterImagePickerFiles, (nv, ov) => {
      if (nv && nv.length) {
        uploadFiles(nv);
        flutterBus.flutterImagePickerFiles = null;
      }
    });

    watch(() => flutterBus.flutterFilePickerFiles, (nv, ov) => {
      if (nv && nv.length) {
        uploadFiles(nv);
        flutterBus.flutterFilePickerFiles = null;
      }
    });

    const labels = ref([] as ISearchLabelInput[]);
    async function handleLabels() {
      const response = await mainStore.onSearchInputLabels('');
      labels.value = response;
      return response;
    }

    function handleMediaDone(f: any) {
      const fileUploads = f.file as IFileUpload[];
      const convId = messagesStore.currentConversation!.info.id;
      handleLabels()
        .then(() => UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.DIALOG_PHOTO, id: convId }, undefined, mainStore.recentInputLabels, true))
        .then((response) => {
          const res = response[0];
          // mediaToCrop.value = {} as IImageCrop;
          return WebsocketService.sendMedia(convId, res.id, props.isGroup);
        });
      // .finally(() => {
      //   mediaUploading.value = false;
      // });
      mediaToCrop.value = {} as IImageCrop;
    }

    function setChooserType(chooserType) {
      currentChooserType.value = chooserType;
      const { nativeApp } = window as any;

      if (nativeApp && chooserType) {
        nativeApp.setChooserType(chooserType);
      }
    }

    function handleRetake() {
      setChooserType(currentChooserType.value);
      mediaToCrop.value = {} as IImageCrop;
      (mediaInputRef!.value! as HTMLInputElement).click();
    }

    function autosize() {
      let newText = message.value;
      newText = newText.replace(/(^\w|\.\s\w|!\s\w|\?\s\w)/gm, (letter) => letter.toUpperCase());
      message.value = newText;
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        input.value!.style.height = '36px';
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        input.value!.style.height = `${input.value!.scrollHeight}px`;
      });
    }

    /**
     * Updates the value of `message` for Android devices when the user types in the input field.
     * This is necessary to enable the send button after the first character is entered.
     */
    function updateMessageForAndroid(event: Event) {
      const { value } = event.target as HTMLInputElement;
      message.value = value;
    }

    function onInput(event: Event) {
      autosize();
      if (mainStore.isAndroid) {
        updateMessageForAndroid(event);
      }
    }

    function focusInput() {
      const inpEl = document.getElementById('input-field'); // using getElementById cause vue ref has errors
      if (inpEl) {
        inpEl.focus();
      }
    }

    function onFocus() {
      setTimeout(() => {
        emit('onFocus');
      }, 100);
    }

    function onSend() {
      emit('send', message.value);
      message.value = '';
      autosize();
      focusInput();
    }

    function showAddMenu() {
      if (props.disabled) {
        return;
      }
      addMenuPopUp.value.isOpen = true;
    }

    function shareFile() {
      if (props.disabled) {
        return;
      }
      fileSelectorPopUp.value.isOpen = true;
    }

    function shareMedia() {
      if (props.disabled) {
        return;
      }
      mediaSelectorPopUp.value.isOpen = true;
    }

    function shareCase() {
      if (props.disabled) {
        return;
      }
      showCaseSelector.value = true;
    }

    function openTakePhoto(chooserType) {
      if (chooserType) {
        currentChooserType.value = chooserType;
      }
      mediaInputRef.value.click();
    }

    function openPhoneMedia() {
      phoneMedia.value.click();
    }

    function onEnter(event) {
      if (!mainStore.isMobile && event.key === 'Enter' && !event.shiftKey) {
        emit('send', message.value);
        message.value = '';
        autosize();
      } else if (event.key === 'Enter' && event.shiftKey) {
        message.value += '\n';
        event.preventDefault(); // prevent default behavior of Enter key
      }
    }

    function cancelUpload() {
      UploadService.isAbortUpload = true;
      mediaUploading.value = false;
    }

    onMounted(() => {
      if (!props.noSearch) {
        handleLabels();
      }
    });

    return {
      message,
      mediaLoadedKey,
      mediaToCrop,
      mediaInputRef,
      showMediaSelector,
      showFileSelector,
      showCaseSelector,
      mediaUploading,
      addMenuPopUp,
      mediaSelectorPopUp,
      mainStore,
      fileSelectorPopUp,
      phoneMedia,
      loaderText,
      uploadInfo,
      handleMediaCancel,
      handleMediaDone,
      handleRetake,
      addMedia,
      onSend,
      showAddMenu,
      openTakePhoto,
      shareCase,
      shareFile,
      shareMedia,
      openPhoneMedia,
      onEnter,
      onInput,
      uploadFile,
      cancelUpload,
      onFocus,
      input,
      autosize,
      labels,
    };
  },
});
